import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExpandableListRow } from "@/components/shared/ExpandableListRow";
import { ResponsiveDetailGrid } from "@/components/shared/ResponsiveDetailGrid";
import { StatusChip } from "@/components/shared/StatusChip";
import { SlideOverPanel } from "@/components/shared/SlideOverPanel";
import { StateCard } from "@/components/shared/StateCard";
import { PageHeader } from "@/components/shared/PageHeader";
import { RefreshingIndicator } from "@/components/shared/RefreshingIndicator";
import {
  SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
  SmsConsentConfirmation,
} from "@/components/shared/SmsConsentConfirmation";
import { TableSkeleton } from "@/components/shared/TableSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  changeApprovalRecipient,
  resendApprovalLink,
  sendApprovalReminder,
} from "@/lib/approvals";
import { invalidateTicketData } from "@/lib/query-invalidation";
import { type ClientItem, type ProjectItem } from "@/lib/projects-clients";
import {
  API_TICKET_STATUSES,
  formatCurrency,
  mapApiStatusToUi,
  type ApiTicketStatus,
} from "@/lib/tickets";
import {
  Search,
  Bell,
  Loader2,
  MoreHorizontal,
  Send,
  Eye,
  User,
  FileText,
  SlidersHorizontal,
  Clock3,
  AlertTriangle,
  Inbox,
} from "lucide-react";
import { useApprovalDetail, useApprovalsQuery, useClients, useProjects } from "@/hooks/use-app-queries";

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit", year: "numeric" });

const STATUS_FILTERS: Array<{ label: string; value: ApiTicketStatus }> = [
  { label: "Pending Approval", value: "PENDING_APPROVAL" },
  { label: "Needs Clarification", value: "NEEDS_CLARIFICATION" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Approved", value: "APPROVED" },
];

type LocalFilters = {
  projectId: string;
  clientId: string;
  agingBucket: "" | "0-12h" | "12-36h" | "36-72h" | "72h+";
  statuses: ApiTicketStatus[];
  neverOpened: boolean;
};

const defaultStatuses: ApiTicketStatus[] = ["PENDING_APPROVAL"];
const defaultFilters: LocalFilters = {
  projectId: "",
  clientId: "",
  agingBucket: "",
  statuses: defaultStatuses,
  neverOpened: false,
};

const toDateText = (value?: string | null, fallback = "—") => (value ? DATE_FORMAT.format(new Date(value)) : fallback);
const toDateTimeText = (value?: string | null, fallback = "—") =>
  value ? new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : fallback;

const getAgingHours = (sentAt?: string | null, fallbackDays = 0) => {
  if (!sentAt) return Math.max(0, Math.floor(fallbackDays * 24));
  const diffMs = Math.max(0, Date.now() - new Date(sentAt).getTime());
  return Math.floor(diffMs / (1000 * 60 * 60));
};

const formatAgingDisplay = (sentAt?: string | null, fallbackDays = 0) => {
  const hours = getAgingHours(sentAt, fallbackDays);
  if (hours > 72) {
    const days = Math.floor(hours / 24);
    return `${days}d`;
  }
  return `${hours}h`;
};

const agingClass = (hours: number) => {
  if (hours >= 36) return "text-destructive bg-destructive/10";
  if (hours >= 12) return "text-orange-700 bg-orange-100";
  if (hours >= 3) return "text-warning bg-warning/10";
  return "text-foreground bg-muted/50";
};

function mapEventLabel(eventType: string) {
  switch (eventType) {
    case "TICKET_CREATED":
      return "Ticket created";
    case "DRAFT_CREATED":
      return "Draft created";
    case "SENT_FOR_APPROVAL":
      return "Sent for approval";
    case "APPROVAL_EMAIL_SENT":
      return "Approval email sent";
    case "APPROVAL_SMS_SENT":
      return "Approval SMS sent";
    case "APPROVAL_LINK_RESENT":
      return "Approval link resent";
    case "APPROVAL_LINK_SMS_RESENT":
      return "Approval SMS resent";
    case "REMINDER_SENT":
      return "Reminder sent";
    case "REMINDER_SMS_SENT":
      return "Reminder SMS sent";
    case "SMS_STATUS_QUEUED":
      return "SMS queued";
    case "SMS_STATUS_SENT":
      return "SMS sent";
    case "SMS_STATUS_DELIVERED":
      return "SMS delivered";
    case "SMS_STATUS_FAILED":
      return "SMS failed";
    case "SMS_STATUS_UNDELIVERED":
      return "SMS undelivered";
    case "RECIPIENT_CHANGED":
      return "Recipient changed";
    case "APPROVAL_LINK_OPENED":
      return "Opened";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "CLARIFICATION_REQUESTED":
      return "Clarification requested";
    case "CLARIFICATION_RESPONSE_SENT":
      return "Clarification response sent";
    case "COMMENT_ADDED":
      return "Comment added";
    case "DELETED":
      return "Deleted";
    default:
      return eventType.replaceAll("_", " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }
}

export default function Approvals() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const [search, setSearch] = useState(searchParams.get("search") ?? "");

  const [filtersOpen, setFiltersOpen] = useState(false);
  const initialStatusesParam = searchParams.get("statuses");
  const initialStatuses = initialStatusesParam
    ? initialStatusesParam
        .split(",")
        .map((value) => value.trim().toUpperCase())
        .filter((value): value is ApiTicketStatus => API_TICKET_STATUSES.includes(value as ApiTicketStatus))
    : defaultStatuses;
  const initialFilters: LocalFilters = {
    projectId: searchParams.get("projectId") ?? "",
    clientId: searchParams.get("clientId") ?? "",
    agingBucket: (searchParams.get("agingBucket") as LocalFilters["agingBucket"]) ?? "",
    statuses: initialStatuses.length ? initialStatuses : defaultStatuses,
    neverOpened: searchParams.get("neverOpened") === "true",
  };
  const [draftFilters, setDraftFilters] = useState<LocalFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<LocalFilters>(initialFilters);

  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);

  const [changeRecipientOpen, setChangeRecipientOpen] = useState(false);
  const [smsConsentConfirmed, setSmsConsentConfirmed] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);

  const approvalsQuery = useApprovalsQuery({
    search,
    projectId: appliedFilters.projectId,
    clientId: appliedFilters.clientId,
    agingBucket: appliedFilters.agingBucket,
    statuses: appliedFilters.statuses,
    neverOpened: appliedFilters.neverOpened,
    startDate: searchParams.get("startDate") || undefined,
    endDate: searchParams.get("endDate") || undefined,
  });
  const projectsQuery = useProjects();
  const clientsQuery = useClients();
  const items = approvalsQuery.data ?? [];
  const projects = projectsQuery.data ?? [];
  const clients = clientsQuery.data ?? [];
  const detailQuery = useApprovalDetail(selectedApprovalId);
  const detail = detailQuery.data ?? null;
  const detailUsesSms =
    detail?.deliveryMethod === "SMS" || detail?.deliveryMethod === "EMAIL_AND_SMS";
  const detailLoading = detailQuery.isLoading && !detailQuery.data;
  const loading = approvalsQuery.isLoading && !approvalsQuery.data;
  const error = approvalsQuery.error instanceof Error ? approvalsQuery.error.message : null;
  const isRefreshing =
    (approvalsQuery.isFetching && Boolean(approvalsQuery.data)) ||
    (projectsQuery.isFetching && Boolean(projectsQuery.data)) ||
    (clientsQuery.isFetching && Boolean(clientsQuery.data));

  useEffect(() => {
    if (!detail) return;
    setRecipientName(detail.recipientName || "");
    setRecipientEmail(detail.recipientEmail || "");
    setSmsConsentConfirmed(false);
    setSmsConsentError("");
  }, [detail]);

  useEffect(() => {
    if (!detailQuery.error || !selectedApprovalId) return;
    toast({
      title: "Could not load approval details",
      description: detailQuery.error instanceof Error ? detailQuery.error.message : "Request failed",
      variant: "destructive",
    });
    setSelectedApprovalId(null);
  }, [detailQuery.error, selectedApprovalId, toast]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (search.trim()) next.set("search", search.trim());
    else next.delete("search");
    if (appliedFilters.projectId) next.set("projectId", appliedFilters.projectId);
    else next.delete("projectId");
    if (appliedFilters.clientId) next.set("clientId", appliedFilters.clientId);
    else next.delete("clientId");
    if (appliedFilters.agingBucket) next.set("agingBucket", appliedFilters.agingBucket);
    else next.delete("agingBucket");
    if (appliedFilters.neverOpened) next.set("neverOpened", "true");
    else next.delete("neverOpened");
    if (appliedFilters.statuses.length) next.set("statuses", appliedFilters.statuses.join(","));
    else next.delete("statuses");
    setSearchParams(next, { replace: true });
  }, [search, appliedFilters.projectId, appliedFilters.clientId, appliedFilters.agingBucket, appliedFilters.neverOpened, appliedFilters.statuses.join(",")]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.projectId) count += 1;
    if (appliedFilters.clientId) count += 1;
    if (appliedFilters.agingBucket) count += 1;
    if (appliedFilters.neverOpened) count += 1;
    if (appliedFilters.statuses.join(",") !== defaultStatuses.join(",")) count += 1;
    return count;
  }, [appliedFilters]);

  const sendReminder = async (approvalId: string) => {
    if (sendingReminderId === approvalId) return;
    const usesSms = detailQuery.data?.id === approvalId &&
      (detailQuery.data.deliveryMethod === "SMS" ||
        detailQuery.data.deliveryMethod === "EMAIL_AND_SMS");
    if (usesSms && !smsConsentConfirmed) {
      setSmsConsentError(SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE);
      toast({
        title: SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
        variant: "destructive",
      });
      return;
    }
    setSendingReminderId(approvalId);
    try {
      await sendApprovalReminder(
        approvalId,
        usesSms ? { smsConsentConfirmed } : undefined,
      );
      toast({ title: "Reminder sent" });
      await invalidateTicketData(queryClient);
      if (selectedApprovalId === approvalId) await detailQuery.refetch();
    } catch (e) {
      toast({
        title: "Reminder failed",
        description: e instanceof Error ? e.message : "Please retry.",
        variant: "destructive",
      });
    } finally {
      setSendingReminderId((current) => (current === approvalId ? null : current));
    }
  };

  const resendLink = async (approvalId: string) => {
    const usesSms = detailQuery.data?.id === approvalId &&
      (detailQuery.data.deliveryMethod === "SMS" ||
        detailQuery.data.deliveryMethod === "EMAIL_AND_SMS");
    if (usesSms && !smsConsentConfirmed) {
      setSmsConsentError(SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE);
      toast({
        title: SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
        variant: "destructive",
      });
      return;
    }
    try {
      await resendApprovalLink(
        approvalId,
        usesSms ? { smsConsentConfirmed } : undefined,
      );
      toast({ title: "Link resent" });
      await invalidateTicketData(queryClient);
      if (selectedApprovalId === approvalId) await detailQuery.refetch();
    } catch (e) {
      toast({
        title: "Resend failed",
        description: e instanceof Error ? e.message : "Please retry.",
        variant: "destructive",
      });
    }
  };

  const saveRecipientChange = async () => {
    const approvalId = detail?.id ?? selectedApprovalId;
    if (!approvalId) return;
    if (!recipientEmail.includes("@")) {
      toast({ title: "Valid recipient email is required", variant: "destructive" });
      return;
    }
    setSavingRecipient(true);
    try {
      await changeApprovalRecipient(approvalId, {
        recipientName: recipientName.trim() || undefined,
        recipientEmail: recipientEmail.trim(),
      });
      toast({ title: "Recipient updated" });
      setChangeRecipientOpen(false);
      await invalidateTicketData(queryClient);
      await detailQuery.refetch();
    } catch (e) {
      toast({
        title: "Recipient update failed",
        description: e instanceof Error ? e.message : "Please retry.",
        variant: "destructive",
      });
    } finally {
      setSavingRecipient(false);
    }
  };

  const copyApprovalLink = async () => {
    if (!detail?.approvalLink) {
      toast({ title: "Approval link unavailable", variant: "destructive" });
      return;
    }
    try {
      await navigator.clipboard.writeText(detail.approvalLink);
      toast({ title: "Approval link copied" });
    } catch {
      toast({
        title: "Copy failed",
        description: "Unable to copy on this device/browser.",
        variant: "destructive",
      });
    }
  };

  const renderActionButtons = () => {
    if (!detail) return null;
    const uiStatus = mapApiStatusToUi(detail.status);
    const reminderLabel = `Send reminder #${(detail.reminderCount || 0) + 1}`;
    const isSendingReminder = sendingReminderId === detail.id;

    if (uiStatus === "pending") {
      return (
        <div className="space-y-2">
          {detailUsesSms ? (
            <SmsConsentConfirmation
              checked={smsConsentConfirmed}
              onCheckedChange={(checked) => {
                setSmsConsentConfirmed(checked);
                if (checked) setSmsConsentError("");
              }}
              error={smsConsentError}
            />
          ) : null}
          <Button
            size="sm"
            className="w-full gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90"
            onClick={() => void sendReminder(detail.id)}
            disabled={isSendingReminder}
          >
            {isSendingReminder ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
            {isSendingReminder ? "Sending..." : reminderLabel}
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => setChangeRecipientOpen(true)}>
            <User className="h-3.5 w-3.5" /> Change Recipient
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => navigate(`/tickets/${detail.ticketId}`)}>
            <FileText className="h-3.5 w-3.5" /> View Full Ticket
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => void copyApprovalLink()}>
            <FileText className="h-3.5 w-3.5" /> Copy approval link
          </Button>
        </div>
      );
    }

    if (uiStatus === "clarification") {
      return (
        <div className="space-y-2">
          {detailUsesSms ? (
            <SmsConsentConfirmation
              checked={smsConsentConfirmed}
              onCheckedChange={(checked) => {
                setSmsConsentConfirmed(checked);
                if (checked) setSmsConsentError("");
              }}
              error={smsConsentError}
            />
          ) : null}
          <Button size="sm" className="w-full gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => void resendLink(detail.id)}>
            <Send className="h-3.5 w-3.5" /> Send clarification follow-up
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => navigate(`/tickets/${detail.ticketId}`)}>
            <FileText className="h-3.5 w-3.5" /> View Full Ticket
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => void copyApprovalLink()}>
            <FileText className="h-3.5 w-3.5" /> Copy approval link
          </Button>
        </div>
      );
    }

    if (uiStatus === "rejected") {
      return (
        <div className="space-y-2">
          <Button size="sm" className="w-full gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => navigate(`/tickets/${detail.ticketId}`)}>
            <Eye className="h-3.5 w-3.5" /> View rejection / revise ticket
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => void resendLink(detail.id)}>
            <Send className="h-3.5 w-3.5" /> Resend after revision
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => navigate(`/tickets/${detail.ticketId}`)}>
            <FileText className="h-3.5 w-3.5" /> View Full Ticket
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => void copyApprovalLink()}>
            <FileText className="h-3.5 w-3.5" /> Copy approval link
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => navigate(`/tickets/${detail.ticketId}`)}>
          <FileText className="h-3.5 w-3.5" /> View Full Ticket
        </Button>
        <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => void copyApprovalLink()}>
          <FileText className="h-3.5 w-3.5" /> Copy approval link
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={t("pages.approvals.title")}
        description={t("pages.approvals.description")}
      />

      {error ? (
        <StateCard
          icon={AlertTriangle}
          tone="error"
          title="Approval queue is unavailable"
          description={error}
          primaryActionLabel="Retry"
          onPrimaryAction={() => void approvalsQuery.refetch()}
        />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex flex-wrap items-center gap-3 md:flex-1">
              <div className="relative max-w-md w-full flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search approvals..."
                  className="pl-9 h-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <RefreshingIndicator visible={isRefreshing} />
            </div>
            <Button variant="outline" size="sm" className="gap-1.5 sm:ml-auto" onClick={() => setFiltersOpen(true)}>
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
          </div>

          {loading ? (
            <TableSkeleton rows={5} columns={8} />
          ) : items.length === 0 ? (
            <StateCard
              icon={Inbox}
              title="No approvals found"
              description="No approval records match your current filters."
              primaryActionLabel="View all approvals"
              onPrimaryAction={() => setAppliedFilters(defaultFilters)}
              secondaryActionLabel="Go to Tickets"
              onSecondaryAction={() => navigate("/tickets")}
            />
          ) : (
            <>
              <div className="hidden min-[1140px]:block rounded-lg border bg-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="bg-muted/35 px-4 py-3 text-left font-medium text-[#0C1F4F]">Ticket #</th>
                      <th className="border-l border-border/60 px-4 py-3 text-left font-medium text-[#0C1F4F]">Recipient</th>
                      <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-left font-medium text-[#0C1F4F]">Project</th>
                      <th className="border-l border-border/60 px-4 py-3 text-left font-medium text-[#0C1F4F]">Sent date</th>
                      <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-left font-medium text-[#0C1F4F]">Last opened</th>
                      <th className="border-l border-border/60 px-4 py-3 text-center font-medium text-[#0C1F4F]">Aging</th>
                      <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-left font-medium text-[#0C1F4F]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => setSelectedApprovalId(item.id)}
                      >
                        <td className="bg-muted/35 px-4 py-3 text-sm font-normal text-muted-foreground">
                          <button
                            className="hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tickets/${item.ticketId}`);
                            }}
                          >
                            {item.ticketNumber}
                          </button>
                        </td>
                        <td className="border-l border-border/50 px-4 py-3">
                          <div className="text-foreground">{item.recipientName || "Approver"}</div>
                          <div className="text-xs text-muted-foreground">{item.recipientEmail}</div>
                        </td>
                        <td className="border-l border-border/50 bg-muted/35 px-4 py-3 text-muted-foreground">{item.project?.name ?? "—"}</td>
                        <td className="border-l border-border/50 px-4 py-3 text-muted-foreground">{toDateText(item.sentAt)}</td>
                        <td className="border-l border-border/50 bg-muted/35 px-4 py-3">
                          {item.lastOpenedAt ? (
                            <span className="text-muted-foreground">{toDateText(item.lastOpenedAt)}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                              <Clock3 className="h-3 w-3" /> Never opened
                            </span>
                          )}
                        </td>
                        <td className="border-l border-border/50 px-4 py-3 text-center">
                          <span
                            className={`inline-flex min-w-11 items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold ${agingClass(getAgingHours(item.sentAt, item.agingDays))}`}
                          >
                            {formatAgingDisplay(item.sentAt, item.agingDays)}
                          </span>
                        </td>
                        <td className="border-l border-border/50 bg-muted/35 px-4 py-3"><StatusChip status={mapApiStatusToUi(item.status)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="hidden min-[767px]:block min-[1140px]:hidden space-y-3">
                {items.map((item) => (
                  <ExpandableListRow
                    key={`tablet-${item.id}`}
                    controlLayout="column"
                    primary={
                      <div className="space-y-1 min-w-0">
                        <button
                          className="font-semibold text-foreground hover:underline"
                          onClick={() => navigate(`/tickets/${item.ticketId}`)}
                        >
                          {item.ticketNumber}
                        </button>
                        <div className="truncate text-xs text-muted-foreground" title={`${item.recipientName || "Approver"} · ${item.recipientEmail}`}>
                          {item.recipientName || "Approver"} · {item.recipientEmail}
                        </div>
                      </div>
                    }
                    secondary={
                      <span className="truncate block max-w-[320px]" title={item.project?.name ?? "—"}>
                        {item.project?.name ?? "—"}
                      </span>
                    }
                    status={<StatusChip status={mapApiStatusToUi(item.status)} />}
                    actions={
                      <button
                        type="button"
                        className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary/60"
                        onClick={() => setSelectedApprovalId(item.id)}
                        aria-label="Open approval"
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </button>
                    }
                    details={
                      <ResponsiveDetailGrid
                        items={[
                          { label: "Sent date", value: toDateText(item.sentAt) },
                          { label: "Last opened", value: item.lastOpenedAt ? toDateText(item.lastOpenedAt) : "Never opened" },
                          { label: "Aging", value: formatAgingDisplay(item.sentAt, item.agingDays) },
                        ]}
                      />
                    }
                  />
                ))}
              </div>

              <div className="min-[767px]:hidden space-y-3">
                {items.map((item) => (
                  <div key={item.id} className="rounded-lg border bg-card p-4 space-y-2 cursor-pointer active:bg-muted/50 transition-colors" onClick={() => setSelectedApprovalId(item.id)}>
                    <div className="flex items-center justify-between gap-2">
                      <button
                        className="font-semibold text-foreground hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tickets/${item.ticketId}`);
                        }}
                      >
                        {item.ticketNumber}
                      </button>
                      <StatusChip status={mapApiStatusToUi(item.status)} className="max-w-[160px]" />
                    </div>
                    <p className="text-sm text-foreground">{item.recipientName || "Approver"} — {item.recipientEmail}</p>
                    <div className="flex items-center justify-start text-xs text-muted-foreground">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 ${agingClass(getAgingHours(item.sentAt, item.agingDays))}`}
                      >
                        Aging {formatAgingDisplay(item.sentAt, item.agingDays)}
                      </span>
                    </div>
                    {!item.lastOpenedAt && <div className="text-xs text-warning">Never opened</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <SlideOverPanel
        open={!!selectedApprovalId}
        onClose={() => {
          setSelectedApprovalId(null);
        }}
        title={detail?.ticketNumber || "Approval details"}
      >
        {detailLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-7 w-28" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          detail && (
            <div className="space-y-5">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-[#0C1F4F]">Quick Actions</h4>
                {detailQuery.isFetching && detail ? <RefreshingIndicator visible /> : null}
                {renderActionButtons()}
              </div>

              <div className="space-y-2">
                <StatusChip status={mapApiStatusToUi(detail.status)} />
                <h3 className="mt-2 font-medium text-[#0C1F4F]">{detail.title}</h3>
                <div className="grid grid-cols-2 gap-2 text-sm pt-2">
                  <div><span className="text-muted-foreground">Project:</span> <span className="text-foreground">{detail.project?.name ?? "—"}</span></div>
                  <div><span className="text-muted-foreground">Value:</span> <span className="font-semibold text-foreground">{formatCurrency(detail.value)}</span></div>
                  <div><span className="text-muted-foreground">Client:</span> <span className="text-foreground">{detail.client?.name ?? "—"}</span></div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-2 text-sm">
                <h4 className="text-sm font-semibold text-[#0C1F4F]">Recipient</h4>
                <p><span className="text-muted-foreground">Name:</span> {detail.recipientName || "Approver"}</p>
                <p><span className="text-muted-foreground">Email:</span> {detail.recipientEmail}</p>
                <p><span className="text-muted-foreground">Sent date:</span> {toDateTimeText(detail.sentAt)}</p>
                <p><span className="text-muted-foreground">Last opened:</span> {toDateTimeText(detail.lastOpenedAt, "Never opened")}</p>
                <p><span className="text-muted-foreground">Last reminder:</span> {toDateTimeText(detail.lastReminderAt, "—")}</p>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-[#0C1F4F]">Timeline</h4>
                <div className="space-y-2 text-sm">
                  <div className="rounded-md bg-muted/40 p-2.5">
                    <p className="text-foreground">Sent for approval</p>
                    <p className="text-xs text-muted-foreground">{toDateTimeText(detail.sentAt)}</p>
                  </div>
                  {detail.lastOpenedAt ? (
                    <div className="rounded-md bg-muted/40 p-2.5">
                      <p className="text-foreground">Opened</p>
                      <p className="text-xs text-muted-foreground">{toDateTimeText(detail.lastOpenedAt)}</p>
                    </div>
                  ) : null}
                  {detail.lastReminderAt ? (
                    <div className="rounded-md bg-muted/40 p-2.5">
                      <p className="text-foreground">Last reminder sent</p>
                      <p className="text-xs text-muted-foreground">{toDateTimeText(detail.lastReminderAt)}</p>
                    </div>
                  ) : null}
                  {!detail.lastOpenedAt ? (
                    <div className="rounded-md border border-warning/30 bg-warning/5 p-2.5 text-xs text-warning">
                      Never opened yet.
                    </div>
                  ) : null}
                  {detail.events.map((event) => (
                    <div key={event.id} className="rounded-md bg-muted/40 p-2.5">
                      <p className="text-foreground">{mapEventLabel(event.eventType)}</p>
                      {event.eventType === "COMMENT_ADDED" && (event.payload as { comment?: string } | null)?.comment ? (
                        <p className="text-xs text-foreground mt-1">
                          {(event.payload as { comment?: string }).comment}
                        </p>
                      ) : null}
                      <p className="text-xs text-muted-foreground">
                        {toDateTimeText(event.happenedAt)} · {event.actorName || event.actorEmail || "system"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )
        )}
      </SlideOverPanel>

      <SlideOverPanel open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filter Approvals">
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Project</label>
            <div className="flex flex-wrap gap-1.5">
              {projects.map((project) => (
                <button
                  key={project.id}
                  className={`text-xs border rounded-full px-2.5 py-1 transition-colors ${draftFilters.projectId === project.id ? "bg-accent/15 text-accent border-accent/40" : "text-muted-foreground hover:bg-muted"}`}
                  onClick={() =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      projectId: prev.projectId === project.id ? "" : project.id,
                    }))
                  }
                >
                  {project.name}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Client</label>
            <div className="flex flex-wrap gap-1.5">
              {clients.map((client) => (
                <button
                  key={client.id}
                  className={`text-xs border rounded-full px-2.5 py-1 transition-colors ${draftFilters.clientId === client.id ? "bg-accent/15 text-accent border-accent/40" : "text-muted-foreground hover:bg-muted"}`}
                  onClick={() =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      clientId: prev.clientId === client.id ? "" : client.id,
                    }))
                  }
                >
                  {client.name}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Aging Bucket</label>
            <div className="flex flex-wrap gap-1.5">
                {[
                { label: "0–12 hours", value: "0-12h" as const },
                { label: "12–36 hours", value: "12-36h" as const },
                { label: "36–72 hours", value: "36-72h" as const },
                { label: "72+ hours", value: "72h+" as const },
              ].map((bucket) => (
                <button
                  key={bucket.value}
                  className={`text-xs border rounded-full px-2.5 py-1 transition-colors ${draftFilters.agingBucket === bucket.value ? "bg-accent/15 text-accent border-accent/40" : "text-muted-foreground hover:bg-muted"}`}
                  onClick={() =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      agingBucket: prev.agingBucket === bucket.value ? "" : bucket.value,
                    }))
                  }
                >
                  {bucket.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Status</label>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_FILTERS.map((status) => (
                <button
                  key={status.value}
                  className={`text-xs border rounded-full px-2.5 py-1 transition-colors ${draftFilters.statuses.includes(status.value) ? "bg-accent/15 text-accent border-accent/40" : "text-muted-foreground hover:bg-muted"}`}
                  onClick={() =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      statuses: prev.statuses.includes(status.value)
                        ? prev.statuses.filter((s) => s !== status.value)
                        : [...prev.statuses, status.value],
                    }))
                  }
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-foreground">Recipient</Label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draftFilters.neverOpened}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, neverOpened: e.target.checked }))}
              />
              Never opened
            </label>
          </div>

          <div className="flex gap-2 pt-3">
            <Button
              className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => {
                setAppliedFilters(draftFilters);
                setFiltersOpen(false);
              }}
            >
              Apply
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDraftFilters(defaultFilters);
                setAppliedFilters(defaultFilters);
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      </SlideOverPanel>

      <Dialog open={changeRecipientOpen} onOpenChange={setChangeRecipientOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Recipient</DialogTitle>
            <DialogDescription>Confirm the new recipient for this approval link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} type="email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeRecipientOpen(false)}>Cancel</Button>
            <Button onClick={() => void saveRecipientChange()} disabled={savingRecipient} className="bg-accent text-accent-foreground hover:bg-accent/90">
              {savingRecipient ? "Saving..." : "Update Recipient"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
